import { useEffect, useState } from "react";
import { AlertTriangle, ArrowLeft, Check, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, gbp, ukDate } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

const fmtQty = (q) => (Number.isInteger(q) ? String(q) : String(Math.round(q * 1000) / 1000));

function PriceMeta({ t }) {
  const when = t.price_source === "price_list" ? "Manual Uploaded Price" : `invoice${t.invoice_number ? ` #${t.invoice_number}` : ""}`;
  return <span className="text-xs text-slate-500">{gbp(t.price)} / {t.unit || "unit"}{t.pack_size ? ` · ${t.pack_size}` : ""} · {ukDate(t.price_date)} · {when}{t.vat_rate ? ` · VAT ${t.vat_rate}%` : " · VAT 0%"}</span>;
}

function OptionLine({ line, optionId }) {
  const s = line.source;
  const t = line.target;
  const id = `${optionId}-${s.supplier_product_id}`;
  if (!t) {
    return (
      <li className="px-3 py-2.5 bg-rose-50/60" data-testid={`merge-line-unavailable-${id}`}>
        <p className="text-sm font-medium text-slate-900 truncate">{s.description}</p>
        <p className="text-xs text-slate-500">Need {fmtQty(s.quantity)} {s.unit || "×"}{s.pack_size ? ` · ${s.pack_size}` : ""} · currently {s.supplier_name} {gbp(s.net)}</p>
        <p className="text-xs font-semibold text-rose-700 flex items-center gap-1 mt-1"><AlertTriangle className="h-3.5 w-3.5" /> {line.status === "unavailable" ? "Product not available from this supplier" : line.reason}</p>
      </li>
    );
  }
  return (
    <li className="px-3 py-2.5" data-testid={`merge-line-${id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 truncate">{t.description}{line.status === "available" && s.description !== t.description ? <span className="text-xs text-slate-400 font-normal"> · for {s.description}</span> : null}</p>
          <p className="text-xs text-slate-600">
            {fmtQty(t.quantity)} × {t.unit || "unit"}{t.pack_size ? ` (${t.pack_size})` : ""}
            {t.required ? <span className="text-slate-400"> · covers {t.required}</span> : null}
            {t.rounded_up ? <span className="ml-1 inline-block rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">rounded up to whole packs</span> : null}
          </p>
          <PriceMeta t={t} />
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-mono font-semibold text-slate-900">{gbp(t.net)}</p>
          <p className="text-[11px] text-slate-400 font-mono">{gbp(t.gross)} inc VAT</p>
        </div>
      </div>
    </li>
  );
}

function OptionCard({ option, letter, current, keepUnmatched, onPick }) {
  const total = keepUnmatched ? option.selected_total : option.moved_total;
  const canMerge = option.complete || keepUnmatched;
  return (
    <section className={`rounded-2xl border ${option.cheapest ? "border-emerald-500 ring-2 ring-emerald-100" : "border-slate-200"} bg-white overflow-hidden`} data-testid={`merge-option-${option.supplier_id}`}>
      <header className="px-4 pt-4 pb-3 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Option {letter}</p>
            <h3 className="text-base font-bold text-slate-900 leading-tight">Order everything from {option.supplier_name}</h3>
          </div>
          {option.cheapest && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-xs font-semibold px-2.5 py-1" data-testid={`merge-cheapest-${option.supplier_id}`}><Trophy className="h-3.5 w-3.5" /> Cheapest</span>}
        </div>
        <div className="mt-3 flex items-end justify-between gap-3">
          <div>
            <p className="text-2xl font-bold font-mono text-slate-900" data-testid={`merge-total-${option.supplier_id}`}>{gbp(total.net)}</p>
            <p className="text-xs text-slate-500">ex VAT · VAT {gbp(total.vat)} · <span className="font-medium text-slate-700">{gbp(total.gross)} inc VAT</span></p>
          </div>
          <div className="text-right text-xs">
            {option.difference_net > 0 && <p className="text-rose-700 font-semibold" data-testid={`merge-diff-${option.supplier_id}`}>+{gbp(option.difference_net)} vs cheapest{option.complete ? "" : " (full order)"}</p>}
            {option.cheapest && option.difference_net === 0 && <p className="text-emerald-700 font-semibold">Best price</p>}
            <p className={option.vs_current_net < 0 ? "text-emerald-700" : option.vs_current_net > 0 ? "text-slate-500" : "text-slate-400"}>{option.vs_current_net < 0 ? `Saves ${gbp(-option.vs_current_net)}` : option.vs_current_net > 0 ? `+${gbp(option.vs_current_net)}` : "Same as"} vs current basket ({gbp(current.net)})</p>
          </div>
        </div>
        {!option.complete && (
          <p className="mt-3 text-xs rounded-lg bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2 flex items-start gap-1.5" data-testid={`merge-incomplete-${option.supplier_id}`}>
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{option.kept_count} of {option.lines.length} product{option.lines.length === 1 ? "" : "s"} not available from {option.supplier_name}. {keepUnmatched ? `Total above includes ${gbp(option.kept_total.net)} kept with the original supplier${option.kept_count === 1 ? "" : "s"}.` : "This total covers only the available products — not the whole order."}</span>
          </p>
        )}
      </header>
      <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {option.lines.map((l) => <OptionLine key={l.source.supplier_product_id} line={l} optionId={option.supplier_id} />)}
      </ul>
      <footer className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
        <p className="text-xs text-slate-600">Revised basket total <span className="font-mono font-semibold text-slate-900">{gbp(option.basket_total.net)}</span> ex VAT</p>
        <Button size="sm" disabled={!canMerge} onClick={() => onPick(option)} className={option.cheapest ? "bg-emerald-600 hover:bg-emerald-700" : ""} variant={option.cheapest ? "default" : "outline"} data-testid={`merge-pick-${option.supplier_id}`}>Merge into {option.supplier_name}</Button>
      </footer>
    </section>
  );
}

function ConfirmStep({ option, keepUnmatched, busy, onBack, onConfirm }) {
  const total = keepUnmatched ? option.selected_total : option.moved_total;
  const moved = option.lines.filter((l) => l.status === "available");
  const kept = option.lines.filter((l) => !l.target);
  return (
    <div data-testid="merge-confirm-step" className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-sm text-slate-600">You are about to update your basket:</p>
        <ul className="mt-2 text-sm text-slate-900 space-y-1">
          <li data-testid="merge-confirm-moved">{moved.length ? <>• Move <strong>{moved.length}</strong> product{moved.length === 1 ? "" : "s"} to <strong>{option.supplier_name}</strong>{option.lines.filter((l) => l.status === "same_supplier").length ? ` (${option.lines.filter((l) => l.status === "same_supplier").length} already with them)` : ""}</> : <>• Nothing to move — {option.lines.filter((l) => l.status === "same_supplier").length} product{option.lines.filter((l) => l.status === "same_supplier").length === 1 ? " is" : "s are"} already with <strong>{option.supplier_name}</strong></>}</li>
          {kept.length > 0 && <li data-testid="merge-confirm-kept">• Keep <strong>{kept.length}</strong> product{kept.length === 1 ? "" : "s"} with the original supplier{kept.length === 1 ? "" : "s"}: {kept.map((l) => l.source.description).join(", ")}</li>}
          {moved.some((l) => l.target.rounded_up) && <li className="text-amber-800">• Some quantities were rounded up to whole packs</li>}
        </ul>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-end justify-between">
          <div>
            <p className="text-xs text-slate-500">Selected suppliers after merge</p>
            <p className="text-xl font-mono font-bold text-slate-900" data-testid="merge-confirm-total">{gbp(total.net)} <span className="text-xs font-sans font-normal text-slate-500">ex VAT · {gbp(total.gross)} inc VAT</span></p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Full revised basket</p>
            <p className="text-base font-mono font-semibold text-slate-900" data-testid="merge-confirm-basket-total">{gbp(option.basket_total.net)}</p>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-3">Only the order basket changes. Invoices, product records and equivalent links are never modified.</p>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onBack} disabled={busy} data-testid="merge-confirm-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <Button onClick={onConfirm} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="merge-confirm-apply"><Check className="h-4 w-4 mr-1" /> {busy ? "Applying…" : "Confirm merge"}</Button>
      </div>
    </div>
  );
}

export default function MergeOrdersDialog({ open, onOpenChange, orderId, supplierIds, onApplied }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [keepUnmatched, setKeepUnmatched] = useState(false);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  const idsKey = supplierIds.join(",");
  useEffect(() => {
    if (!open || !orderId || supplierIds.length < 2) return;
    setPreview(null); setError(null); setPicked(null); setKeepUnmatched(false);
    api.post(`/ordering/orders/${orderId}/merge-preview`, { supplier_ids: idsKey.split(",") })
      .then((r) => setPreview(r.data))
      .catch((e) => setError(errorMessage(e)));
  }, [open, orderId, idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const anyIncomplete = preview?.options.some((o) => !o.complete);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ordering/orders/${orderId}/merge-apply`, { supplier_ids: supplierIds, target_supplier_id: picked.supplier_id, keep_unmatched: keepUnmatched });
      onApplied(data);
      toast.success(`Basket updated — ${data.merge.moved_count} product${data.merge.moved_count === 1 ? "" : "s"} now with ${picked.supplier_name}${data.merge.kept_count ? `, ${data.merge.kept_count} kept with original supplier${data.merge.kept_count === 1 ? "" : "s"}` : ""}`);
      onOpenChange(false);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="p-0 gap-0 w-screen h-[100dvh] max-w-none rounded-none sm:rounded-2xl sm:w-full sm:max-w-3xl sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden" data-testid="merge-orders-dialog">
        <DialogHeader className="px-4 sm:px-6 pt-5 pb-3 border-b border-slate-100 text-left">
          <DialogTitle className="text-lg">Merge Orders</DialogTitle>
          <DialogDescription>
            {picked ? `Confirm merging into ${picked.supplier_name}` : preview ? `Comparing ${preview.options.map((o) => o.supplier_name).join(" vs ")} · current ${gbp(preview.current.total.net)} ex VAT for these suppliers` : "Comparing linked equivalent products at each supplier…"}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 bg-slate-50/50">
          {error && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="merge-error">{error}</p>}
          {!preview && !error && <p className="text-sm text-slate-500" data-testid="merge-loading">Calculating…</p>}
          {preview && picked && <ConfirmStep option={picked} keepUnmatched={keepUnmatched} busy={busy} onBack={() => setPicked(null)} onConfirm={apply} />}
          {preview && !picked && (
            <>
              {anyIncomplete && (
                <label className="flex items-start gap-2.5 text-sm text-slate-800 bg-white border border-slate-200 rounded-xl px-3 py-2.5 cursor-pointer" data-testid="merge-keep-unmatched">
                  <Checkbox checked={keepUnmatched} onCheckedChange={(v) => setKeepUnmatched(!!v)} className="mt-0.5" data-testid="merge-keep-unmatched-checkbox" />
                  <span><span className="font-medium">Keep unmatched products with their original suppliers</span><span className="block text-xs text-slate-500">Lets you merge even when a supplier can't cover everything. Totals then include the kept lines so you see the full picture.</span></span>
                </label>
              )}
              <div className="grid gap-4 md:grid-cols-2">
                {preview.options.map((o, idx) => <OptionCard key={o.supplier_id} option={o} letter={String.fromCharCode(65 + idx)} current={preview.current.total} keepUnmatched={keepUnmatched} onPick={setPicked} />)}
              </div>
              <p className="text-xs text-slate-400">Prices are the latest invoice (or supplier price list) prices, converted through pack sizes and units per box. Delivery charges and minimum order values are not included.</p>
            </>
          )}
        </div>
        {!picked && (
          <div className="px-4 sm:px-6 py-3 border-t border-slate-100 bg-white flex justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="merge-cancel">Cancel</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
